import React, { useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'
import { FormApi } from 'final-form'
import { isNil } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { isNoteSubmittedForAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  AIDraftStatus,
  EntityTypes,
} from '@lyrahealth-inc/shared-app-logic/src/models/appointmentSummary/AppointmentSummary'
import { Assignment } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'

import AssignmentAISummaryText from './AssignmentAISummaryText'
import { useGetAssignmentSummaryQuery } from './data/assignmentsApi'
import { isLCxProvider, isProviderUS } from '../../common/utils/utils'
import { getAuthUser } from '../../data/auth/authSelectors'
import { useGetFeedbackQuery, useGetNotesQuery } from '../clientNotes/data/notesApi'
import PanelFeedbackSection from '../clients/PanelFeedbackSection'

interface AssignmentAISummaryProps {
  selectedAssignment: Assignment
  episodeId: string
  intakeAppointmentId?: number
  isAssignmentAnIntake: (assignmentName?: string) => boolean
}

const PanelFeedbackContainer = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  borderTopColor: colors.dividerSecondary,
  borderTopStyle: 'solid',
  borderTopWidth: '1px',
  marginTop: '0px',
  marginBottom: spacing['16px'],
}))

const AssignmentAISummary: React.FC<AssignmentAISummaryProps> = ({
  selectedAssignment,
  episodeId,
  intakeAppointmentId,
  isAssignmentAnIntake,
}) => {
  const { colors } = useTheme()
  const feedbackFormRef = useRef<FormApi<Dict>>()
  const user = useSelector(getAuthUser)

  const { showAIClientIntakeSummary, enableAIClientIntakeSummaryFetch } = useFlags()

  const isLCxProviderCheck = isLCxProvider(user)
  const isProviderUSCheck = isProviderUS(user)

  const { data: allNotes } = useGetNotesQuery(
    episodeId
      ? {
          episodeId: episodeId,
        }
      : skipToken,
  )

  const isIntakeSessionNoteSubmitted = useMemo(
    () => isNoteSubmittedForAppointment(allNotes, intakeAppointmentId),
    [allNotes, intakeAppointmentId],
  )

  const shouldFetchSummary =
    showAIClientIntakeSummary &&
    enableAIClientIntakeSummaryFetch &&
    isAssignmentAnIntake(selectedAssignment?.content?.name) &&
    !isIntakeSessionNoteSubmitted &&
    isProviderUSCheck &&
    isLCxProviderCheck

  const { data: clientIntakeSummaryResponse, isLoading: isLoadingSummary } = useGetAssignmentSummaryQuery(
    shouldFetchSummary && selectedAssignment?.id && user?.id
      ? {
          assignmentId: selectedAssignment.id,
          providerId: user.id,
          statusOnly: false,
        }
      : skipToken,
  )

  const {
    data: feedback,
    isLoading: isLoadingFeedback,
    isSuccess: hasFetchedFeedback,
  } = useGetFeedbackQuery(
    shouldFetchSummary && selectedAssignment?.id && user?.id
      ? {
          providerId: user.id,
          entityId: selectedAssignment.id,
        }
      : skipToken,
  )

  if (!shouldFetchSummary) {
    return null
  }

  if (isLoadingSummary || isNil(clientIntakeSummaryResponse)) {
    return null
  }

  return (
    <>
      <AssignmentAISummaryText clientIntakeSummaryResponse={clientIntakeSummaryResponse} />
      {clientIntakeSummaryResponse &&
        clientIntakeSummaryResponse?.status === AIDraftStatus.COMPLETE &&
        !isLoadingFeedback &&
        hasFetchedFeedback &&
        intakeAppointmentId && (
          <PanelFeedbackContainer>
            <PanelFeedbackSection
              appointmentId={intakeAppointmentId.toString()}
              backgroundColor={colors.backgroundElement}
              displayTextTime={10000}
              feedbackFormRef={feedbackFormRef}
              entityId={selectedAssignment?.id}
              entityType={EntityTypes.ASSIGNMENTS}
              loopEnvelopeLottie={false}
              promptType={clientIntakeSummaryResponse?.prompt_type ?? ''}
              feedback={feedback}
            />
          </PanelFeedbackContainer>
        )}
    </>
  )
}

export default AssignmentAISummary
